/**
 * Collection Registry — Maps all DB-loaded Typesense collections to their
 * save*ToSearch functions for bulk load and CDC.
 *
 * For bulk load: calls save*ToSearch(tx, id, collectionName, { returnDoc: true })
 * For CDC: calls save*ToSearch(tx, id, collectionName) — sends to SQS as normal
 *
 * Single source of truth — same function, same code path, same output.
 */

import { DbTransaction } from "@tc/middy-decorators"

import { saveAgencyToSearch } from "../builders/save-agency-to-search"
import { saveBottleSizesToSearch } from "../builders/save-bottle-sizes-to-search"
import { saveCountriesToSearch } from "../builders/save-countries-to-search"
import { saveEUIngredientsToSearch } from "../builders/save-eu-ingredients-to-search"
import {
  getExportCertificatesBulkData,
  saveExportCertificatesToSearch,
} from "../builders/save-export-certificates-to-search"
import { saveLanguageToSearch } from "../builders/save-language-to-search"
import { savePackageTypesToSearch } from "../builders/save-package-types-to-search"
import {
  getPortBulkData,
  savePortToSearch,
} from "../builders/save-port-to-search"
import { saveRefCaptionToSearch } from "../builders/save-ref-caption-to-search"
import { saveRefFieldToSearch } from "../builders/save-ref-field-to-search"
import { saveRefItemHistoryToSearch } from "../builders/save-ref-item-history-to-search"
import { saveRefItemToSearch } from "../builders/save-ref-item-to-search"
import { saveRefListToSearch } from "../builders/save-ref-list-to-search"
import { saveRefPhytoBotanicalNameToSearch } from "../builders/save-ref-phyto-botanical-name-to-search"
import { saveRefProcessGroupCaptionToSearch } from "../builders/save-ref-process-group-caption-to-search"
import { saveRefProcessGroupToSearch } from "../builders/save-ref-process-group-to-search"
import { saveRefProcessToSearch } from "../builders/save-ref-process-to-search"
import { saveRefUnitToSearch } from "../builders/save-ref-unit-to-search"
import { saveOcaUserToSearch } from "../builders/save-oca-user-to-search"
import { saveRegistrationToSearch } from "../builders/save-registrations-to-search"
import { saveSupportingDocumentToSearch } from "../builders/save-supporting-document-to-search"
import { saveTemplateToSearch } from "../builders/save-template-to-search"
import {
  getTransferRequestBulkData,
  saveTransferRequestToSearch,
} from "../builders/save-transfer-request-to-search"
import {
  getTransferProductsBulkData,
  saveTransferProductsToSearch,
} from "../builders/save-transfer-products-to-search"
import { saveUnLocodeToSearch, getUnLocodeBulkData } from "../builders/save-un-locode-to-search"
import { saveWineColoursToSearch } from "../builders/save-wine-colours-to-search"
import { saveWineEUCategoriesToSearch } from "../builders/save-wine-eu-categories-to-search"
import { saveWineGeographicIndicationsToSearch } from "../builders/save-wine-geographic-indications-to-search"
import { saveWineGrapeVarietiesToSearch } from "../builders/save-wine-grape-varieties-to-search"
import { saveWineStylesToSearch } from "../builders/save-wine-styles-to-search"

import {
  getEcB2gBulkData,
  saveEcB2gToSearch,
} from "../builders/save-ec-b2g-to-search"

type SaveToSearchFn = (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
) => Promise<void>

/** Typesense collection visibility prefix — controls API key access scoping */
export enum CollectionVisibility {
  Public = "public",
  Private = "private",
  EC = "ec",
  IC = "ic",
}

/** All Typesense collection names */
export enum CollectionName {
  Agency = "agency",
  BottleSizes = "bottle-sizes",
  BusinessAreas = "business-areas",
  Caption = "caption",
  CertificateDeliveryMethods = "certificate-delivery-methods",
  CertificateTypes = "certificate-types",
  CnCodes = "cn-codes",
  CommodityTypes = "commodity-types",
  Countries = "countries",
  DeAlcoholised = "de-alcoholised",
  DestinationMarkets = "destination-markets",
  DocumentTypes = "document-types",
  Eligibilities = "eligibilities",
  EstarLaboratories = "estar-laboratories",
  EstarOrganisations = "estar-organisations",
  EstarWineTests = "estar-wine-tests",
  EUIngredients = "eu-wine-ingredients",
  ExportCertificates = "export-certificates",
  Exporters = "exporters",
  Field = "field",
  FreeSalePremises = "free-sale-premises",
  HsCodes = "hs-codes",
  ImportCertificates = "import-certificates",
  ImportCertificateTypes = "import-certificate-types",
  IppcAcronyms = "ippc-acronyms",
  Item = "item",
  ItemHistory = "item-history",
  Language = "language",
  List = "list",
  OcaUsers = "oca-users",
  OrganicPremises = "organic-premises",
  PackageTypes = "package-types",
  Pesticides = "pesticides",
  PhytoBotanicalName = "phyto-botanical-name",
  Port = "port",
  Process = "process",
  ProcessGroup = "process-group",
  ProcessGroupCaption = "process-group-caption",
  ProcessTypes = "process-types",
  ProductFields = "product-fields",
  Products = "products",
  Registrations = "registrations",
  SupportingDocuments = "supporting-documents",
  Templates = "templates",
  TransferProducts = "transfer-products",
  TransferRequests = "transfer-requests",
  Unit = "unit",
  UnLocode = "un-locode",
  Verifiers = "verifiers",
  WineBrClassifications = "wine-br-classifications",
  WineChemTests = "wine-chem-tests",
  WineColours = "wine-colours",
  WineEUCategories = "wine-eu-categories",
  WineGeographicIndications = "wine-geographic-indications",
  WineGrapeVarieties = "wine-grape-varieties",
  WineLabDetail = "wine-lab-detail",
  WineSpecialProcesses = "wine-special-processes",
  WineStyles = "wine-styles",
  Wsmps = "wsmps",
}

type GetBulkDataFn = (
  tx: DbTransaction,
  ids: readonly string[],
) => Promise<Map<string, unknown>>

export interface CollectionSource {
  readonly schemaName: string
  readonly tableName: string
  readonly saveToSearch: SaveToSearchFn
  readonly idType: "int" | "uuid"
  readonly pkColumn?: string
  /** Optional bulk data fetcher — batch queries for a chunk of IDs, returns Map<id, doc>. */
  readonly getBulkData?: GetBulkDataFn
}

/**
 * Type-safe source constructor.
 * Forces the schema+table+builder to be declared together in one place,
 * making it obvious when a builder doesn't match its source table.
 * Usage: source("core", "export_request", saveExportCertificatesToSearch, "uuid")
 */
const source = (
  schemaName: string,
  tableName: string,
  saveToSearch: SaveToSearchFn,
  idType: "int" | "uuid",
  pkColumn?: string,
  getBulkData?: GetBulkDataFn,
): CollectionSource => ({
  schemaName,
  tableName,
  saveToSearch,
  idType,
  pkColumn,
  getBulkData,
})

export interface CollectionRegistryEntry {
  readonly collectionName: CollectionName
  /** One or more DB source tables that feed into this Typesense collection */
  readonly sources: ReadonlyArray<CollectionSource>
  readonly cluster: "transfers" | "exports" | "reference"
}

export const COLLECTION_REGISTRY: ReadonlyArray<CollectionRegistryEntry> = [
  // core
  {
    collectionName: CollectionName.TransferRequests,
    sources: [
      source(
        "core",
        "transfer_request",
        saveTransferRequestToSearch,
        "uuid",
        undefined,
        getTransferRequestBulkData,
      ),
    ],
    cluster: "transfers",
  },
  {
    collectionName: CollectionName.TransferProducts,
    sources: [
      source(
        "core",
        "product_item",
        saveTransferProductsToSearch,
        "uuid",
        undefined,
        getTransferProductsBulkData,
      ),
    ],
    cluster: "transfers",
  },
  {
    collectionName: CollectionName.ExportCertificates,
    sources: [
      source(
        "core",
        "export_request",
        saveExportCertificatesToSearch,
        "uuid",
        undefined,
        getExportCertificatesBulkData,
      ),
      source(
        "ec",
        "certificate_request",
        saveEcB2gToSearch,
        "uuid",
        undefined,
        getEcB2gBulkData,
      ),
    ],
    cluster: "exports",
  },
  {
    collectionName: CollectionName.SupportingDocuments,
    sources: [
      source(
        "core",
        "supporting_document",
        saveSupportingDocumentToSearch,
        "uuid",
      ),
    ],
    cluster: "exports",
  },

  // reference-data
  {
    collectionName: CollectionName.Countries,
    sources: [source("reference", "country", saveCountriesToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Item,
    sources: [source("reference", "item", saveRefItemToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.ItemHistory,
    sources: [
      source("reference", "item_history", saveRefItemHistoryToSearch, "int"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Field,
    sources: [
      source(
        "reference",
        "field",
        saveRefFieldToSearch,
        "int",
        "field_unique_id",
      ),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Unit,
    sources: [source("reference", "unit", saveRefUnitToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Caption,
    sources: [source("reference", "caption", saveRefCaptionToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.List,
    sources: [
      source("reference", "list", saveRefListToSearch, "int", "list_code"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Port,
    sources: [
      source(
        "reference",
        "port",
        savePortToSearch,
        "int",
        undefined,
        getPortBulkData,
      ),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Language,
    sources: [source("reference", "language", saveLanguageToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.EUIngredients,
    sources: [
      source("reference", "eu_ingredient", saveEUIngredientsToSearch, "int"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.WineColours,
    sources: [source("reference", "item", saveWineColoursToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.WineStyles,
    sources: [source("reference", "item", saveWineStylesToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.WineGrapeVarieties,
    sources: [
      source("reference", "item", saveWineGrapeVarietiesToSearch, "int"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.WineGeographicIndications,
    sources: [
      source("reference", "item", saveWineGeographicIndicationsToSearch, "int"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.WineEUCategories,
    sources: [source("reference", "item", saveWineEUCategoriesToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Process,
    sources: [source("reference", "process", saveRefProcessToSearch, "int")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.ProcessGroup,
    sources: [
      source("reference", "process_group", saveRefProcessGroupToSearch, "int"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.ProcessGroupCaption,
    sources: [
      source(
        "reference",
        "process_group_caption",
        saveRefProcessGroupCaptionToSearch,
        "int",
      ),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.PhytoBotanicalName,
    sources: [
      source(
        "reference",
        "phyto_botanical_name",
        saveRefPhytoBotanicalNameToSearch,
        "int",
      ),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.UnLocode,
    sources: [source("reference", "port", saveUnLocodeToSearch, "int", undefined, getUnLocodeBulkData)],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.PackageTypes,
    sources: [source("reference", "item", savePackageTypesToSearch, "int")],
    cluster: "reference",
  },

  // sf-party-v2
  {
    collectionName: CollectionName.Registrations,
    sources: [
      source("party", "registration", saveRegistrationToSearch, "uuid"),
    ],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.Agency,
    sources: [source("party", "agency", saveAgencyToSearch, "uuid")],
    cluster: "reference",
  },
  {
    collectionName: CollectionName.OcaUsers,
    sources: [source("party", "user", saveOcaUserToSearch, "uuid")],
    cluster: "reference",
  },

  // b2g
  {
    collectionName: CollectionName.BottleSizes,
    sources: [source("reference", "item", saveBottleSizesToSearch, "int")],
    cluster: "reference",
  },

  // template
  {
    collectionName: CollectionName.Templates,
    sources: [
      source("template", "template_version", saveTemplateToSearch, "uuid"),
    ],
    cluster: "reference",
  },
] as const

// --- Lookup helpers ---

const registryMap = new Map<string, CollectionRegistryEntry>(
  COLLECTION_REGISTRY.map((e) => [e.collectionName, e]),
)

/** Get registry entry for a collection */
export const getCollectionEntry = (
  collectionName: string,
): CollectionRegistryEntry => {
  const entry = registryMap.get(collectionName)
  if (!entry)
    throw new Error(`Collection "${collectionName}" not found in registry`)
  return entry
}

/** Get all unique Typesense collection names */
export const getCollectionNames = (): CollectionName[] =>
  COLLECTION_REGISTRY.map((e) => e.collectionName)

export const getCollectionsByCluster = (
  cluster: CollectionRegistryEntry["cluster"],
) => COLLECTION_REGISTRY.filter((e) => e.cluster === cluster)

/**
 * Flattened view: one row per (collection, source table) pair.
 * Used by the bulk load extract phase which iterates per source table.
 */
export interface FlatSourceEntry {
  readonly collectionName: CollectionName
  readonly schemaName: string
  readonly tableName: string
  readonly saveToSearch: SaveToSearchFn
  readonly idType: "int" | "uuid"
  readonly pkColumn: string
  readonly getBulkData?: GetBulkDataFn
}

const flatEntries: FlatSourceEntry[] = COLLECTION_REGISTRY.flatMap((entry) =>
  entry.sources.map((src) => ({
    collectionName: entry.collectionName,
    schemaName: src.schemaName,
    tableName: src.tableName,
    saveToSearch: src.saveToSearch,
    idType: src.idType,
    pkColumn: src.pkColumn ?? "id",
    getBulkData: src.getBulkData,
  })),
)

export const getCollectionsByTable = (tableName: string) =>
  flatEntries.filter((e) => e.tableName === tableName)

/**
 * Groups by source table (schema.table).
 * Collections sharing a table are extracted together.
 *
 * Example:
 *   "reference.item" → [item, wine-colours, wine-styles, ..., bottle-sizes]
 *   "core.export_request" → [export-certificates]
 *   "ec.certificate_request" → [export-certificates]   ← same collection, different table
 */
export const getTableGroups = (): Map<string, FlatSourceEntry[]> => {
  const groups = new Map<string, FlatSourceEntry[]>()
  for (const entry of flatEntries) {
    const key = `${entry.schemaName}.${entry.tableName}`
    const group = groups.get(key) ?? []
    group.push(entry)
    groups.set(key, group)
  }
  return groups
}

/**
 * Get all unique source tables for bulk extraction.
 * The Extract Lambda iterates over these, not over collections.
 */
export const getExtractTables = (): Array<{
  schemaName: string
  tableName: string
  idType: "int" | "uuid"
  pkColumn: string
  collections: FlatSourceEntry[]
}> => {
  const groups = getTableGroups()
  return [...groups.entries()].map(([, collections]) => ({
    schemaName: collections[0].schemaName,
    tableName: collections[0].tableName,
    idType: collections[0].idType,
    pkColumn: collections[0].pkColumn,
    collections,
  }))
}
